import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Filter, Columns3, X, Plus, GripVertical, Search } from "lucide-react";

const OPS = {
  text: [["contains", "contains"], ["is", "is"], ["is_not", "is not"], ["empty", "is empty"], ["not_empty", "is not empty"]],
  number: [["gt", ">"], ["gte", "≥"], ["lt", "<"], ["lte", "≤"], ["is", "="], ["empty", "is empty"], ["not_empty", "is not empty"]],
  date: [["after", "on/after"], ["before", "before"], ["last_days", "in last N days"], ["empty", "is empty"], ["not_empty", "is not empty"]],
  pick: [["is", "is"], ["is_not", "is not"], ["empty", "is empty"], ["not_empty", "is not empty"]],
};
export const opsFor = (f) => (f?.type === "number" ? OPS.number : ["date", "datetime"].includes(f?.type) ? OPS.date
  : ["stage", "owner", "select"].includes(f?.type) ? OPS.pick : OPS.text);

export const ATTR_FIELDS = [
  { key: "utm.utm_source", label: "UTM source", type: "text" }, { key: "utm.utm_campaign", label: "UTM campaign", type: "text" },
  { key: "utm.utm_medium", label: "UTM medium", type: "text" }, { key: "updated_at", label: "Last updated", type: "datetime" },
  { key: "next_task", label: "Next task", type: "next_task" },
];

function MultiPick({ opts, value, set, i }) {
  const vals = Array.isArray(value) ? value : value ? [value] : [];
  const toggle = (o) => set({ value: vals.includes(o) ? vals.filter((x) => x !== o) : [...vals, o] });
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="px-2 py-1 rounded border text-xs w-full text-left truncate" data-testid={`filter-${i}-value`}>{vals.length ? vals.join(", ") : "— pick —"}</button>
      </PopoverTrigger>
      <PopoverContent className="w-56 bg-[var(--panel)] border-[var(--border-strong)] p-1 max-h-64 overflow-auto" data-testid={`filter-${i}-options`}>
        {opts.map((o) => (
          <label key={o} className="flex items-center gap-2 text-xs px-2 py-1 rounded cursor-pointer hover:bg-[var(--hover)]">
            <input type="checkbox" checked={vals.includes(o)} onChange={() => toggle(o)} data-testid={`filter-${i}-opt-${o}`} />{o}
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function ValueIn({ f, c, set, stages, users, i }) {
  if (["empty", "not_empty"].includes(c.op)) return null;
  const cls = "px-2 py-1 rounded border text-xs w-full";
  const opts = f?.type === "stage" ? stages : f?.type === "owner" ? users.map((u) => u.email) : f?.type === "select" ? f.options || [] : null;
  if (opts) return <MultiPick opts={opts} value={c.value} set={set} i={i} />;
  const type = c.op === "last_days" || f?.type === "number" ? "number" : ["date", "datetime"].includes(f?.type) ? "date" : "text";
  return <input type={type} className={cls} value={c.value ?? ""} onChange={(e) => set({ value: e.target.value })} data-testid={`filter-${i}-value`} />;
}

export function FilterBuilder({ filters, onChange, fields, stages, users, match = "all", onMatch }) {
  const all = [...fields, ...ATTR_FIELDS];
  const set = (i, p) => onChange(filters.map((f, k) => (k === i ? { ...f, ...p } : f)));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={`btn text-xs ${filters.length ? "border-[var(--ai)] text-[var(--ai)]" : ""}`} data-testid="filter-btn"><Filter size={13} />Filter{filters.length ? ` · ${filters.length}` : ""}</button>
      </PopoverTrigger>
      <PopoverContent className="w-[480px] max-w-[95vw] bg-[var(--panel)] border-[var(--border-strong)] p-3 space-y-2" data-testid="filter-pop">
        {!filters.length && <div className="text-xs text-neutral-500">No filters. Showing all leads.</div>}
        {filters.length > 1 && (
          <div className="flex items-center gap-2 text-xs text-neutral-400" data-testid="filter-match">Match
            {[["all", "all filters"], ["any", "any filter"]].map(([k, l]) => (
              <button key={k} className={`px-2 py-0.5 rounded border ${match === k ? "border-[var(--ai)] text-[var(--ai)]" : "border-[var(--border)]"}`} onClick={() => onMatch(k)} data-testid={`filter-match-${k}`}>{l}</button>
            ))}
          </div>
        )}
        {filters.map((c, i) => {
          const f = all.find((x) => x.key === c.field);
          return (
            <div key={i} className="grid grid-cols-[minmax(0,1fr)_100px_minmax(0,1.3fr)_20px] gap-1 items-center">
              <select className="px-2 py-1 rounded border text-xs" value={c.field} onChange={(e) => set(i, { field: e.target.value, op: opsFor(all.find((x) => x.key === e.target.value))[0][0], value: "" })} data-testid={`filter-${i}-field`}>
                {all.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select>
              <select className="px-2 py-1 rounded border text-xs" value={c.op} onChange={(e) => set(i, { op: e.target.value })} data-testid={`filter-${i}-op`}>
                {opsFor(f).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <ValueIn f={f} c={c} set={(p) => set(i, p)} stages={stages} users={users} i={i} />
              <button className="btn-ghost p-0.5" onClick={() => onChange(filters.filter((_, k) => k !== i))} data-testid={`filter-${i}-del`}><X size={12} /></button>
            </div>
          );
        })}
        <div className="flex gap-2 pt-1">
          <button className="btn text-xs" onClick={() => onChange([...filters, { field: "stage", op: "is", value: "" }])} data-testid="filter-add"><Plus size={12} />Add filter</button>
          {filters.length > 0 && <button className="btn-ghost btn text-xs" onClick={() => onChange([])} data-testid="filter-clear">Clear</button>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ColumnPicker({ columns, onChange, fields }) {
  const all = [...fields, ...ATTR_FIELDS];
  const [q, setQ] = useState("");
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const label = (k) => all.find((f) => f.key === k)?.label || k;
  const hit = (l) => l.toLowerCase().includes(q.trim().toLowerCase());
  const drop = (to) => {
    if (drag === null || drag === to) return;
    const a = [...columns];
    const [x] = a.splice(drag, 1);
    a.splice(to, 0, x);
    onChange(a);
  };
  const hidden = all.filter((f) => !columns.includes(f.key) && hit(f.label)).sort((a, b) => a.label.localeCompare(b.label));
  return (
    <Popover onOpenChange={(o) => { if (!o) setQ(""); }}>
      <PopoverTrigger asChild><button className="btn text-xs" data-testid="columns-btn"><Columns3 size={13} />Columns</button></PopoverTrigger>
      <PopoverContent className="w-72 bg-[var(--panel)] border-[var(--border-strong)] p-2 max-h-[28rem] overflow-auto" data-testid="columns-pop">
        <div className="flex items-center gap-1.5 px-2 py-1 mb-2 rounded border border-[var(--border-strong)]">
          <Search size={12} className="text-[var(--text-faint)]" />
          <input autoFocus className="flex-1 bg-transparent text-xs outline-none" placeholder="Search columns" value={q} onChange={(e) => setQ(e.target.value)} data-testid="columns-search" />
        </div>
        <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">Shown · drag to reorder</div>
        {columns.map((k, i) => hit(label(k)) && (
          <div key={k} draggable={!q} onDragStart={() => setDrag(i)} onDragOver={(e) => { e.preventDefault(); setOver(i); }}
            onDragEnd={() => { setDrag(null); setOver(null); }} onDrop={() => { drop(i); setDrag(null); setOver(null); }}
            className={`flex items-center gap-1 text-xs py-1 px-1 rounded ${q ? "" : "cursor-grab"} ${over === i && drag !== i ? "bg-[var(--hover)] border-t border-[var(--ai)]" : ""} ${drag === i ? "opacity-40" : ""}`}
            data-testid={`col-shown-${k}`}>
            {!q && <GripVertical size={12} className="text-[var(--text-faint)] shrink-0" />}
            <input type="checkbox" checked onChange={() => onChange(columns.filter((x) => x !== k))} data-testid={`col-toggle-${k}`} />
            <span className="flex-1 truncate">{label(k)}</span>
          </div>
        ))}
        <div className="text-[10px] uppercase tracking-wider text-neutral-500 mt-2 mb-1">Hidden · A–Z</div>
        {!hidden.length && <div className="text-xs text-[var(--text-faint)] px-1">No matches</div>}
        {hidden.map((f) => (
          <label key={f.key} className="flex items-center gap-1 text-xs py-1 px-1 cursor-pointer rounded hover:bg-[var(--hover)]">
            <input type="checkbox" checked={false} onChange={() => onChange([...columns, f.key])} data-testid={`col-toggle-${f.key}`} />{f.label}
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}
